import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import { shopModeApi } from "../../api/shopMode";

/** Which auth plane fetches the bytes (TaskImages pattern). */
export type ReferencePhotoMode = "crm" | "floor";

/**
 * G-15 reference photos, resolved to blob URLs, with the whole lifetime owned
 * here: fetch list → fetch each blob → revoke on unmount or order change.
 *
 * Extracted from `OrderReferencePhotos` so one caller can render the same
 * photos in two places from a SINGLE fetch — the floor builder shows a
 * thumbnail in its order strip and the full set inside the description
 * overlay, and mounting the component twice would pull every image twice over
 * the shop's wifi.
 *
 * A failure resolves to an empty list rather than throwing: the brief is
 * helpful, never a gate.
 */
export function useOrderReferencePhotos(
  orderId: string,
  mode: ReferencePhotoMode = "crm",
): string[] {
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    let stale = false;
    let created: string[] = [];
    const list =
      mode === "floor"
        ? shopModeApi.floorListOrderReferencePhotos
        : api.listOrderReferencePhotos;
    const blob =
      mode === "floor"
        ? shopModeApi.floorOrderReferencePhotoBlobUrl
        : api.fetchOrderReferencePhotoBlobUrl;

    // Drop the previous order's URLs immediately: the cleanup below has
    // already revoked them, so rendering them one more frame shows a broken
    // image rather than a stale one.
    setUrls([]);

    void list(orderId)
      .then((photos) =>
        Promise.all(photos.map((photo) => blob(orderId, photo.id))),
      )
      .then((next) => {
        const resolved = next.filter((url): url is string => Boolean(url));
        if (stale) {
          resolved.forEach((url) => URL.revokeObjectURL(url));
          return;
        }
        created = resolved;
        setUrls(resolved);
      })
      .catch(() => {
        /* the brief is helpful, never blocking */
      });

    return () => {
      stale = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [orderId, mode]);

  return urls;
}
